import React from 'react';
import ApplicationController, { randomId } from '@aha-app/mvc';
import Assistant from '../../models/Assistant';
import {
  Page,
  AiUserAgent,
  type AssistantChat,
  type AssistantMessage,
  type AssistantToolCall,
  type Attachment,
} from 'components/framework/models';
import type { EditorApi } from '../PromptPanel/PromptEditor';
import { chooseFiles } from '../utils/attachmentHandling';
import Agent, {
  type AgentParams,
  type AgentInstances,
  DEFAULT_AGENT_NAME,
} from '../../agents/Agent';
import {
  InstructionScopeItem,
  ReferenceScopeItem,
} from '../PromptPanel/ScopeItems';
import { closeAssistant } from '../utils/openAssistant';
import AssistantContext, {
  getReferenceNumFromUrl,
} from 'javascripts/assistant_context';
import { getToolCallFormData } from '../utils/toolCallFormData';
import { jsonFetch } from 'javascripts/fetch';
import { uniqBy } from 'lodash';
import { dangerouslyShowNoticeMessage } from 'javascripts/messages';

export interface CustomAgent {
  id: string;
  name: string;
  agentScope: string;
  favorite: boolean;
}

export interface Instruction extends CustomAgent {
  instruction: string;
}

export interface Prompt extends CustomAgent {
  prompt: string;
}

// How the assistant is positioned on the page.
export type AssistantDock =
  | 'full-screen' // Assistant fills the viewport.
  | 'full-page' // Assistant filles the page except for the navbar.
  | 'side-panel' // Assistant starts in side panel.
  | 'floating'; // Assistant starts floating.

// The size of the assistant within the dock.
export type AssistantSize =
  | 'toolbar' // Narrow toolbar with minimal content before being used.
  | 'engaged-top' // User has engaged with the assistant (e.g. focused the input).
  | 'engaged-bottom' // User has engaged with the assistant (e.g. focused the input).
  | 'full-height' // Assistant has responses and takes full height of page.
  | 'side-panel' // Assistant is in a side-panel.
  | 'full-screen' // Assistant takes over the whole screen.
  | 'full-page'; // Assistant filles the page except for the navbar.

type ResizeTriggers =
  | 'blur' // The assistant is blurred.
  | 'close-button' // The close button was pressed.
  | 'toggle-size' // Toggle the size of the assistant. What happens depends on the current size.
  | 'full-height' // The full height button was pressed.
  | 'full-screen' // The full screen button was pressed.
  | 'toolbar' // The toolbar button was pressed.
  | 'open-artifact' // An artifact needs to be displayed.
  | 'focus-prompt-editor' // The prompt editor is focused.
  | 'submit-message' // The user submitted a message.
  | 'open-tool-panel' // A tool panel needs to be displayed.
  | 'click-conversation'; // User clicks in the conversation.

type ToolPanel =
  | 'history' // The history panel is open.
  | 'settings' // The settings panel is open.
  | 'plan'; // The plan panel is open.

export interface AssistantViewProps {
  // The agent that should be applied to new conversations in the assistant.
  agentName?: string;
  // The parameters to pass to the agent. The values must be serializable to JSON
  // since they are passed to the server.
  agentParams?: AgentParams;
  // Additional parameters to pass to the agent. These are not passed to the
  // server, but can be used for the agent to interact with the rest of the page
  // it is embedded in. e.g. to manipulate a text editor. Each value should
  // have a correlated value in the `agentParams` object so that the two
  // chats with the same `agent` and `agentParams` also have corresponding
  // `agentInstances` values. e.g. by including the record ID or reference number
  // in `agentParams`.
  agentInstances?: AgentInstances;
  // How the assistant should be docked.
  dock?: AssistantDock;
  // The assistant should focus the prompt editor as soon as it is initially loaded.
  focus?: boolean; // TODO: This is not implemented yet.
  // Token for the chat to load. Providing a token triggers read-only mode.
  token?: string;
  // ID of the chat to load.
  chatId?: RecordId;
}

// A wrapper for attachments that are being uploaded. This is used to show
// the upload progress in the UI.
export type AttachmentStatus = {
  // ID of this entry.
  id: string;
  // Whether the attachment is still being loaded.
  loading: boolean;
  // The actual attachment instance once it is loaded.
  attachment?: Attachment;
};

// Additional context that will be send to server with the next user
// message. It is also visible to the user above the prompt box.
export interface ScopeItem {
  id: RecordId;
  // A function that returns the value of the scope item to send to the server.
  resolve: () => any;
  // A function that returns a React component to render the scope item in the UI.
  render: (() => React.ReactNode) | undefined | null;
}

interface AssistantViewState {
  // The name of the current agent. Here so that views can trigger re-renders when
  // the agent changes.
  agentName: string;
  // The ID of the current agent.
  agentId: string;
  // The front-end state of the assistant, synchronized from the backend.
  model: Assistant;

  // The size the assistant should be displayed at.
  size: AssistantSize;
  // How the assistant is docked when not expanded.
  dock: AssistantDock;
  // Whether the assistant is read-only.
  readOnly: boolean;
  // The currently viewed artifact.
  currentArtifact?: AssistantToolCall;
  // How to display the artifact.
  artifactDisplayMode: 'preview' | 'source';
  // The currently viewed tool panel.
  currentToolPanel?: ToolPanel;
  // The attachment currently being viewed in a popover.
  currentImageAttachment?: Attachment;

  // Attachments on the current prompt.
  promptAttachments: AttachmentStatus[];

  // Scope data to be included with the current prompt.
  scopeItems: Record<string, ScopeItem>;

  // Stored data about layout of the messages to use in computing scrolling.
  layoutData: Record<string, number>;

  // Instructions currently active
  activeInstructions: Instruction[];
  // All instructions that can be used
  instructions: Instruction[];

  // All custom agents that can be used
  customAgents: CustomAgent[];

  // All prompts that can be used
  prompts: Prompt[];

  projectPrefixes: Record<string, boolean>;

  promptPrefix?: string; // Prefix to add to the user message on submission.

  mappedPrompts: {
    favorites: (Prompt | Instruction)[];
    personal: (Prompt | Instruction)[];
    workspace: (Prompt | Instruction)[];
  };
}

export class AssistantViewController extends ApplicationController<
  AssistantViewState,
  AssistantViewProps
> {
  static initialState = {
    size: 'toolbar' as AssistantSize,
    readOnly: false,
    promptAttachments: [],
    scopeItems: {},
    layoutData: {},
    artifactDisplayMode: 'preview',
    activeInstructions: [],
    instructions: [],
    customAgents: [],
    prompts: [],
  };

  // Handle to interact with the prompt editor.
  editorApi: EditorApi | null = null;

  // The agent that applies to the current conversation and new conversations.
  // Should not be in state since an agent implementation may bring in external
  // libraries.
  agent: Agent;

  // The agent name and params to use when creating a new chat.
  initialAgentName: string;
  initialAgentParams: AgentParams;
  initialAgentInstances: AgentInstances;

  lastRecordsOnPage: string | null = null;

  async initialize(props: AssistantViewProps) {
    this.state.dock = props.dock || 'floating';
    if (this.state.dock === 'side-panel') {
      this.state.size = 'side-panel';
    } else if (this.state.dock === 'full-screen') {
      this.state.size = 'full-screen';
    } else if (this.state.dock === 'full-page') {
      this.state.size = 'full-page';
    }

    if (props.focus && this.state.dock === 'floating') {
      this.state.size = 'engaged-top';
    }

    this.initialAgentName = props.agentName || DEFAULT_AGENT_NAME;
    this.initialAgentParams = props.agentParams || { projectId: undefined };
    this.initialAgentInstances = props.agentInstances || {};
    this.state.mappedPrompts = {
      favorites: [],
      personal: [],
      workspace: [],
    };

    this.state.projectPrefixes = window.currentProject?.referencePrefix
      ? {
          [window.currentProject?.referencePrefix]: true,
        }
      : {};

    this.setAgent(props.agentName);
    this.state.model = new Assistant(this);
    this.model.subscribe();

    if (props.token) {
      // Load a read-only shared conversation.
      this.state.readOnly = true;
      await this.model.loadChat(props.token);
      this.setAgent(this.model.currentChat.agent);
    } else if (props.chatId) {
      // Load a specific chat.
      await this.model.loadChat(props.chatId);
      this.setAgent(this.model.currentChat.agent);
    } else {
      // Load the last conversion?
    }

    this.loadInstructionsAndCustomAgents().catch(error => {
      console.error('Error loading instruction options:', error);
    });

    this.setInstructionScopeItems();

    window.addEventListener(
      'aha-assistant:context-changed',
      this.handleAssistantContextChange
    );
    this.loadAssistantGlobalContext();

    document.addEventListener(
      'reloadAiUserAgents',
      this.handleReloadAiUserAgents
    );
  }

  destroy() {
    this.model.unsubscribe();
    window.removeEventListener(
      'aha-assistant:context-changed',
      this.handleAssistantContextChange
    );
    document.removeEventListener(
      'reloadAiUserAgents',
      this.handleReloadAiUserAgents
    );
  }

  get model() {
    return this.state.model;
  }

  handleReloadAiUserAgents = (event: CustomEvent) => {
    void this.loadInstructionsAndCustomAgents().then(() => {
      switch (event.detail.agentType) {
        case 'INSTRUCTION': {
          const instruction = this.state.instructions.find(
            inst => inst.id === event.detail.id
          );
          if (instruction) {
            this.state.activeInstructions = uniqBy(
              [...this.state.activeInstructions, instruction],
              'id'
            );
          }
          break;
        }
        case 'AGENT': {
          this.actionSetAgent(event.detail.id);
          break;
        }
        case 'PROMPT': {
          const prompt = this.state.prompts.find(p => p.id === event.detail.id);
          if (prompt) {
            this.actionSelectPrompt(prompt);
          }
          break;
        }
        default:
          break;
      }
    });
  };

  handleAssistantContextChange = (event: CustomEvent) => {
    this.loadAssistantGlobalContext();
  };

  loadAssistantGlobalContext = () => {
    // Remove all previous references from the Assistant context.
    // List here all keys the could come from the global context
    ['references'].forEach(key => {
      if (this.state.scopeItems[key]) {
        delete this.state.scopeItems[key];
      }
    });

    const references = AssistantContext.instance.context.references || [];

    if (references.length > 0) {
      this.setScopeItem('references', {
        id: randomId() as RecordId,
        resolve: () => references,
        render: () => (
          <>
            {references.map((reference: string) => (
              <ReferenceScopeItem
                key={reference}
                referenceNum={reference}
                onRemove={() => {
                  AssistantContext.instance.removeReferenceFromContext(
                    reference
                  );
                }}
              />
            ))}
          </>
        ),
      });
    }
  };

  getAdditionalScopeData() {
    const data: any = {};

    let recordsOnPage = this.getRecordsOnPage();
    if (recordsOnPage && recordsOnPage !== this.lastRecordsOnPage) {
      const pageRef = getReferenceNumFromUrl(window.location.pathname);
      if (pageRef) {
        recordsOnPage =
          `${getReferenceNumFromUrl(window.location.pathname)},` +
          recordsOnPage;
      }
      this.lastRecordsOnPage = recordsOnPage;
      data.records_on_page = recordsOnPage;
    }

    return data;
  }

  getRecordsOnPage() {
    const drawerPaths = [...document.querySelectorAll('[data-drawer-url]')].map(
      (el: HTMLElement) => el.dataset.drawerUrl
    );
    if (!drawerPaths || drawerPaths.length === 0) {
      return null;
    }

    return [
      ...new Set(
        drawerPaths.map(s => getReferenceNumFromUrl(s)).filter(Boolean)
      ),
    ].join(',');
  }

  actionSetAgent(agentId: string) {
    // If it looks like a numeric ID it is a custom agent
    if (agentId.match(/^\d+$/)) {
      this.initialAgentParams.ai_user_agent_id = agentId;
      this.setAgent('CustomUserAgent', agentId);
    } else {
      delete this.initialAgentParams.ai_user_agent_id;
      this.setAgent(agentId);
    }
  }

  setAgent(agentName?: string, customAgentId?: string) {
    // Find the agent class by name.
    if (!agentName || agentName.length === 0) {
      agentName = window.defaultAgent || DEFAULT_AGENT_NAME;
    }
    let AgentClass = Agent.lookup(agentName);
    if (!AgentClass) {
      console.error(`Agent not found: ${agentName}`);
      AgentClass = Agent.lookup(DEFAULT_AGENT_NAME)!;
    }

    // Check the agent has all the required inputs to actually run.
    if (
      !AgentClass.available(this.initialAgentParams, this.initialAgentInstances)
    ) {
      AgentClass = Agent.lookup(DEFAULT_AGENT_NAME)!;
    }

    this.state.agentName = AgentClass.name;
    this.state.agentId = customAgentId || AgentClass.name;
    this.agent = new AgentClass(
      this,
      this.initialAgentParams,
      this.initialAgentInstances
    );
  }

  // Returns a proxied version of the scope item that will trigger re-render on changes
  setScopeItem(key: string, value: ScopeItem): ScopeItem {
    this.state.scopeItems[key] = value;
    return this.state.scopeItems[key];
  }

  maybeSetLocation(chatId: RecordId) {
    if (this.state.dock === 'full-page') {
      window.history.pushState({}, 'AI assistant', `/ai/${chatId}`);
    }
  }

  // Call `actionResizeByTrigger` rather than calling this directly.
  actionSetSize(size: AssistantSize) {
    this.state.size = size;
  }

  // Centralize all logic for choosing the size of the assistant. This function
  // uses the current state and the trigger to determine what to do.
  actionResizeByTrigger(resizeTrigger: ResizeTriggers) {
    const size = this.state.size;

    switch (resizeTrigger) {
      case 'blur':
        if (size === 'engaged-bottom') this.actionSetSize('toolbar');
        break;

      case 'toggle-size':
        switch (size) {
          case 'toolbar':
          case 'engaged-top':
          case 'engaged-bottom':
            this.actionSetSize('full-height');
            break;
          case 'full-height':
            this.actionSetSize('full-screen');
            break;
          case 'side-panel':
            this.actionSetSize('full-screen');
            break;
          case 'full-screen':
            if (this.state.dock === 'floating') {
              this.actionSetSize('full-height');
            } else {
              this.actionSetSize('side-panel');
            }
            break;
        }
        break;

      case 'close-button':
        if (this.state.dock === 'floating') {
          this.actionSetSize('toolbar');
          this.actionCloseArtifact();
          this.actionShowToolPanel(null);
        } else {
          this.actionSetSize('side-panel');
        }
        this.state.currentArtifact = undefined;
        break;

      case 'full-height':
        if (this.state.dock === 'side-panel') {
          this.actionSetSize('side-panel');
        } else {
          this.actionSetSize('full-height');
        }
        break;

      case 'full-screen':
        this.actionSetSize('full-screen');
        break;

      case 'toolbar':
        this.actionShowToolPanel(null);
        this.actionCloseArtifact();
        this.actionSetSize('toolbar');
        break;

      case 'open-tool-panel':
      case 'open-artifact':
        if (
          !['full-screen', 'full-height', 'side-panel', 'full-page'].includes(
            size
          )
        ) {
          this.actionSetSize('full-height');
        }
        break;

      case 'focus-prompt-editor':
        if (!['full-screen', 'full-page'].includes(this.state.size)) {
          this.actionShowToolPanel(null);
          this.actionCloseArtifact();
        }
        if (size === 'toolbar') {
          if (this.model.emptyChat) {
            this.actionSetSize('engaged-bottom');
          } else {
            this.actionSetSize('full-height');
          }
        }
        break;

      case 'click-conversation':
        if (!['full-screen', 'full-page'].includes(this.state.size)) {
          this.actionShowToolPanel(null);
          this.actionCloseArtifact();
        }
        break;

      case 'submit-message':
        if (
          ['toolbar', 'engaged-bottom', 'engaged-top'].includes(this.state.size)
        ) {
          this.actionSetSize('full-height');
        }
        break;

      default:
        console.error(`Unhandled resize trigger: ${resizeTrigger}`);
    }
  }

  actionClose() {
    // Remove ourself from the DOM.
    closeAssistant();
  }

  actionSubmitPrompt(content: string, parentId?: RecordId) {
    const attachmentData = this.state.promptAttachments
      .map(attachmentStatus => attachmentStatus.attachment)
      .filter(attachment => !!attachment);

    const scopeData = Object.entries(this.state.scopeItems).reduce(
      (result, [key, scopeItem]) => ({ ...result, [key]: scopeItem.resolve() }),
      {}
    );

    // merge the new chat scope data with the current scope data
    Object.assign(scopeData, this.getAdditionalScopeData());

    content = `${
      this.state.promptPrefix ? this.state.promptPrefix : ''
    }${content}`;

    this.state.promptPrefix = undefined;

    // Instructions are sent with the prompt and then not sent on subsequent messages
    this.state.activeInstructions = [];

    this.model
      .sendUserMessage(content, attachmentData, scopeData, {
        agentName: this.agent.name(),
        agentParams: this.initialAgentParams,
        parentId,
      })
      .catch(e => {
        console.error('Error sending user message:', e);
      });
    this.state.promptAttachments = [];
  }

  // Submit a system generated user message, without interfering with the
  // prompt the user is currently writing.
  actionSubmitMessage(content: string) {
    this.model
      .sendUserMessage(
        content,
        [],
        {},
        {
          agentName: this.agent.name(),
          agentParams: this.initialAgentParams,
        }
      )
      .catch(e => {
        console.error('Error sending user message:', e);
      });
  }

  actionNewChat() {
    this.actionCloseArtifact();
    this.setAgent(this.initialAgentName);
    this.state.activeInstructions = [];
    this.state.promptPrefix = undefined;
    this.model.newChat(this.agent.name());
    // NOTE: Restore scope context after creating new chat to prevent losing record context when switching agents
    this.setInstructionScopeItems();
    this.loadAssistantGlobalContext();
  }

  actionShowToolPanel(toolPanel: ToolPanel | undefined | null) {
    if (this.state.currentToolPanel === toolPanel || !toolPanel) {
      this.state.currentToolPanel = undefined;
      return;
    }

    this.actionResizeByTrigger('open-tool-panel');
    this.state.currentToolPanel = toolPanel;
  }

  async actionShowChat(chat: AssistantChat) {
    if (!['full-screen', 'full-page'].includes(this.state.size)) {
      this.actionShowToolPanel(null);
    }
    await this.state.model.loadChat(chat.id);
    this.setAgent(this.state.model.currentChat.agent);
    // this.maybeSetLocation(chat.id); This is where we would change the URL. This turned out to be annoying.
    this.setLastInstructionsFromCurrentChat();
  }

  actionOpenArtifact(toolCall: AssistantToolCall) {
    this.state.artifactDisplayMode = 'preview';
    this.state.currentArtifact = toolCall;
    this.actionResizeByTrigger('open-artifact');
  }

  actionCloseArtifact() {
    this.state.currentArtifact = undefined;
  }

  actionUseSuggestion(content: string, afterMessage?: AssistantMessage) {
    this.model.cancel();
    if (afterMessage) {
      this.actionCloseArtifact(); // Close the artifact in case it was removed from the chat.
      this.model.removeMessagesAfter(afterMessage);
    }
    // Replace the content of the prompt editor with the suggestion.
    if (this.editorApi) {
      this.editorApi.replaceContent(content);
      this.editorApi.focus();
    }
  }

  actionAddSuggestionAsAssistantMessage(content: string, prefix?: string) {
    this.model.addSuggestionAsAssistantMessage(content);
    this.state.promptPrefix = prefix;
  }

  actionSetEditorApi(editorApi: EditorApi | null) {
    this.editorApi = editorApi;
  }

  actionSelectPrompt(prompt: Prompt) {
    if (this.editorApi) {
      this.editorApi.replaceContent(prompt.prompt);
      this.editorApi.focus();
    }
  }

  // Show a modal for adding one or more attachments to upload.
  actionChooseAttachments() {
    chooseFiles(this);
  }

  actionAddAttachmentStatus(attachmentStatus: AttachmentStatus) {
    this.state.promptAttachments = [
      ...this.state.promptAttachments,
      attachmentStatus,
    ];
  }

  actionSetAttachmentStatus(
    attachmentStatus: AttachmentStatus,
    attachment: Attachment
  ) {
    this.state.promptAttachments = this.state.promptAttachments.map(a => {
      if (a.id === attachmentStatus.id) {
        a.attachment = attachment;
        a.loading = false;
      }
      return a;
    });
  }

  actionDeleteAttachment(attachment: AttachmentStatus) {
    this.state.promptAttachments = this.state.promptAttachments.filter(
      a => a.id !== attachment.id
    );
  }

  // Store measured layout data for the controlling layout and scroll positioning.
  actionSetLayoutData(key: string, value: number) {
    this.state.layoutData[key] = value;
  }

  actionSetArtfactDisplayMode(mode: 'preview' | 'source') {
    this.state.artifactDisplayMode = mode;
  }

  loadInstructionsAndCustomAgents = async () => {
    if (!this.projectId) {
      return;
    }

    const response = await jsonFetch(
      `/api/v1/products/${this.projectId}/ai_agents/picker`,
      { method: 'GET' }
    );
    const responseJson = await response.json();

    const customAgents = (responseJson as any)?.agents?.map(option => ({
      id: option.id,
      name: option.name,
      agentScope: option.agent_scope,
      favorite: option.favorite,
      agentType: 'AGENT',
    })) as CustomAgent[];

    const instructions = (responseJson as any)?.instructions?.map(option => ({
      id: option.id,
      name: option.name,
      instruction: option.instruction,
      agentScope: option.agent_scope,
      favorite: option.favorite,
      agentType: 'INSTRUCTION',
    })) as Instruction[];

    const prompts = (responseJson as any)?.prompts?.map(option => ({
      id: option.id,
      name: option.name,
      prompt: option.instruction,
      agentScope: option.agent_scope,
      favorite: option.favorite,
      agentType: 'PROMPT',
    })) as Prompt[];

    if (instructions) {
      this.state.instructions = instructions;
    }
    if (customAgents) {
      this.state.customAgents = customAgents;
    }
    if (prompts) {
      this.state.prompts = prompts;
    }

    const mappedPrompts: {
      favorites: (Prompt | Instruction)[];
      personal: (Prompt | Instruction)[];
      workspace: (Prompt | Instruction)[];
    } = {
      favorites: [],
      personal: [],
      workspace: [],
    };

    this.state.prompts?.map(option => {
      if (option.favorite) {
        mappedPrompts.favorites.push(option);
      }
      if (
        option.agentScope === 'personal' ||
        option.agentScope === 'workspace'
      ) {
        mappedPrompts[option.agentScope].push(option);
      }
    });
    this.state.instructions?.map(option => {
      if (option.favorite) {
        mappedPrompts.favorites.push(option);
      }
      if (
        option.agentScope === 'personal' ||
        option.agentScope === 'workspace'
      ) {
        mappedPrompts[option.agentScope].push(option);
      }
    });

    // Sort the prompts by name
    mappedPrompts.personal.sort((a, b) => a.name.localeCompare(b.name));
    mappedPrompts.workspace.sort((a, b) => a.name.localeCompare(b.name));

    this.state.mappedPrompts = mappedPrompts;
  };

  setLastInstructionsFromCurrentChat() {
    const lastInstructionIds = (this.state.model.lastInstructions() ?? []).map(
      i => i.id
    );

    this.state.activeInstructions = this.state.instructions.filter(
      instruction => lastInstructionIds.includes(instruction.id)
    );
  }

  get projectId(): string | undefined {
    return this.props.agentParams?.projectId;
  }

  get inDiscoveryParent(): boolean {
    return window.currentProject?.isDiscovery || false;
  }

  setInstructionScopeItems() {
    const onRemoveInstruction = (removed: Instruction) => {
      this.state.activeInstructions = this.state.activeInstructions.filter(
        instruction => instruction.id !== removed.id
      );
    };

    this.setScopeItem('instructions', {
      id: randomId() as RecordId,
      resolve: () => this.state.activeInstructions.map(i => i.id),
      render: () => (
        <InstructionScopeItem
          instructions={this.state.activeInstructions}
          onRemove={onRemoveInstruction}
        />
      ),
    });
  }

  actionProcessToolCall(toolCall: AssistantToolCall) {
    this.agent.processToolCall(toolCall);
  }

  actionProvideToolCallData(toolCall: AssistantToolCall) {
    const data = this.agent.provideToolCallData(toolCall);
    if (data) {
      // If the agent provides data for the tool call, we approve it.
      this.model.provideFrontendContent(toolCall, data);
    }
  }

  actionApproveAllToolCalls(
    toolCalls: {
      toolCall: AssistantToolCall;
      data?: Record<string, unknown>;
    }[]
  ) {
    const toolCallsToSendToServer = toolCalls
      .map(({ toolCall, data }) => {
        const processedData = this.agent.beforeApproveToolCall(toolCall, data);
        return { toolCall, data: processedData };
      })
      .filter(({ toolCall, data }) => {
        return this.agent.approveToolCall(toolCall, data);
      });

    if (toolCallsToSendToServer.length > 0) {
      void this.model.approveToolCalls(toolCallsToSendToServer);
    }
  }

  actionApproveToolCall(toolCall, data?: Record<string, unknown>) {
    if (
      !data &&
      (toolCall.content?.panel?.contentType === 'application/vnd.aha.html' ||
        toolCall.content?.preview?.contentType ===
          'application/vnd.aha.askMultipleChoice')
    ) {
      data = getToolCallFormData(toolCall.uniqueId);
    }

    this.actionApproveAllToolCalls([{ toolCall: toolCall, data: data }]);
  }

  actionRejectAllToolCalls(toolCalls: AssistantToolCall[]) {
    const toolCallsToSendToServer = toolCalls.filter(toolCall =>
      this.agent.rejectToolCall(toolCall)
    );
    if (toolCallsToSendToServer.length > 0) {
      void this.model.rejectToolCalls(toolCallsToSendToServer);
    }
  }

  actionRejectToolCall(toolCall) {
    this.actionRejectAllToolCalls([toolCall]);
  }

  async actionUpdateToolCallStatus(
    toolCall: AssistantToolCall,
    newStatus: string
  ) {
    await this.model.updateToolCallStatus(toolCall, newStatus);
  }

  async actionCreateNoteFromArtifact(toolCall) {
    this.addPageIdToToolCall(toolCall, 'loading');
    const page = new Page({
      name:
        toolCall.content?.preview?.title ||
        toolCall.content?.panel?.title ||
        'New Note',
      project: {
        id: this.projectId,
      },
      documentType: Page.DOCUMENT_TYPE_NOTE,
    });

    if (toolCall.content?.panel?.contentType === 'text/markdown') {
      page.descriptionMarkdown = toolCall.content?.panel?.body || '';
    } else {
      page.description = toolCall.content?.panel?.body || '';
    }

    page.inDiscoveryParent = this.inDiscoveryParent;

    await page.save();

    await jsonFetch(`/assistant/tool_calls/${toolCall.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        result_data: {
          page_id: page.id,
        },
      }),
    });

    this.addPageIdToToolCall(toolCall, page.id);

    // open the page in a new tab to allow the user to keep the context of the current assistant
    // const pagePath = Page.pathForId(page.id);
    // if (pagePath) {
    //   window.open(pagePath, '_blank');
    // }
  }

  addPageIdToToolCall(toolCall, pageId: RecordId | string) {
    if (toolCall.content?.data) {
      let existingData: Record<string, unknown> = {};
      if (toolCall.content?.data) {
        existingData = JSON.parse(toolCall.content.data) as Record<
          string,
          unknown
        >;
      }
      existingData.page_id = pageId;
      toolCall.content.data = JSON.stringify(existingData);
    }
  }

  actionCheckProjectPrefix(prefix: string) {
    if (this.state.projectPrefixes[prefix] === undefined) {
      this.state.projectPrefixes[prefix] = false; // Default to false until we check stops the request from being sent multiple times.

      void jsonFetch(
        `/products/valid_reference_prefix?reference_prefix=${prefix}`,
        { method: 'GET' }
      )
        .then(response => response.json())
        .then((data: { valid: boolean }) => {
          this.state.projectPrefixes[prefix] = data.valid;
        });
    }
  }

  actionShowAttachment(attachment: Attachment) {
    if (attachment.contentType?.startsWith('image/')) {
      this.state.currentImageAttachment = attachment;
    }
  }

  actionCloseImageAttachment() {
    this.state.currentImageAttachment = undefined;
  }

  async actionCreatePrompt(prompt: string) {
    const aiPrompt = new AiUserAgent({
      agentType: 'PROMPT',
      descriptionMarkdown: prompt,
      favorite: true,
      name: '<AI_USER_AGENT_NAME>',
    });
    dangerouslyShowNoticeMessage(
      `Created prompt in <a href="/settings/ai_agents?tab=prompts" target="_blank">personal settings</a>.`
    );

    await aiPrompt.save();
  }
}

export default AssistantViewController;
